import { Container, Sprite, Graphics, FillGradient, Texture } from 'pixi.js';
import { assetLoader } from '../core/AssetLoader';
import { SVG_SHIP_META } from './ShipGraphics';

/**
 * Per-ship-class flare sizing derived from SVG metadata.
 * flareHeight = thrustOffsetY * 0.35, flareWidth = flareHeight * 0.5
 */
function getFlareBaseSize(shipClassId: string, sizeMultiplier: number): { w: number; h: number } {
  const meta = SVG_SHIP_META[shipClassId];
  const thrustOffsetY = meta ? meta.thrustOffsetY : 80;
  const scale = meta ? meta.scale : 0.30;
  const h = thrustOffsetY * scale * sizeMultiplier * 1.4;
  const w = h * 0.5;
  return { w: Math.max(w, 6), h: Math.max(h, 8) };
}

// Layer definitions (relative to base size)
const LAYERS = {
  outer: { sizeScale: 2.5, baseAlpha: 0.20, color: '#DD8839' },
  mid:   { sizeScale: 1.0, baseAlpha: 0.50, color: '#20A5A6' },
  core:  { sizeScale: 0.4, baseAlpha: 0.90, color: '#FFFFFF' },
} as const;

type LayerName = keyof typeof LAYERS;
const LAYER_NAMES: LayerName[] = ['outer', 'mid', 'core'];

// Number of flicker frames per layer
const FRAME_COUNT = 3;

/**
 * Generates a single radial-gradient ellipse texture for one flare layer frame.
 * Uses PixiJS 8 FillGradient with radial type.
 */
function generateFlareTexture(
  layerName: LayerName,
  width: number,
  height: number,
  frameIndex: number,
): Texture {
  const cacheKey = `flare-${layerName}-${Math.round(width)}-${Math.round(height)}-f${frameIndex}`;
  const cached = assetLoader.getTexture(cacheKey);
  if (cached) return cached;

  // Slight height variation per frame for flicker
  const heightVariation = 1.0 + (frameIndex - 1) * 0.08; // frames: 0.92, 1.0, 1.08
  const finalH = Math.round(height * heightVariation);
  const finalW = Math.round(width);

  // Padding for the gradient to not clip at edges
  const pad = 4;
  const texW = finalW + pad * 2;
  const texH = finalH + pad * 2;

  const g = new Graphics();

  let gradient: FillGradient;

  if (layerName === 'core') {
    // White center → pale yellow → transparent
    gradient = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.5,
      colorStops: [
        { offset: 0, color: '#FFFFFF' },
        { offset: 0.35, color: '#FFF4CC' },
        { offset: 0.7, color: 'rgba(255,244,204,0.3)' },
        { offset: 1.0, color: 'rgba(255,238,136,0)' },
      ],
      textureSpace: 'local',
    });
  } else if (layerName === 'mid') {
    // Teal center → transparent
    gradient = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.25 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.5,
      colorStops: [
        { offset: 0, color: '#60E8E8' },
        { offset: 0.3, color: '#20A5A6' },
        { offset: 0.7, color: 'rgba(32,165,166,0.2)' },
        { offset: 1.0, color: 'rgba(32,165,166,0)' },
      ],
      textureSpace: 'local',
    });
  } else {
    // Outer bloom: warm gold → transparent
    gradient = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.5,
      colorStops: [
        { offset: 0, color: '#DD8839' },
        { offset: 0.4, color: 'rgba(221,136,57,0.3)' },
        { offset: 1.0, color: 'rgba(221,136,57,0)' },
      ],
      textureSpace: 'local',
    });
  }

  // Draw ellipse filled with the gradient
  g.ellipse(texW / 2, texH / 2, texW / 2, texH / 2);
  g.fill(gradient);

  const texture = assetLoader.generateTexture(g, cacheKey);
  g.destroy();
  return texture;
}

/**
 * Volumetric engine flare effect — 3 layered gradient sprites (outer bloom, mid body, core).
 * Each layer pulses at different frequencies for an organic, living feel.
 * Textures are pre-rendered and cached per ship class.
 */
export class EngineFlare extends Container {
  private outerSprite: Sprite;
  private midSprite: Sprite;
  private coreSprite: Sprite;

  private outerTextures: Texture[] = [];
  private midTextures: Texture[] = [];
  private coreTextures: Texture[] = [];

  private animTime: number = 0;
  private flickerSeed: number;
  private tickCounter: number = 0;

  private baseOuterAlpha: number;
  private baseMidAlpha: number;
  private baseCoreAlpha: number;

  private intensity: number = 1.0;
  private texturesReady: boolean = false;

  private shipClassId: string;
  private sizeMultiplier: number;

  constructor(shipClassId: string, sizeMultiplier: number) {
    super();
    this.shipClassId = shipClassId;
    this.sizeMultiplier = sizeMultiplier;
    this.flickerSeed = Math.random() * Math.PI * 2;

    this.baseOuterAlpha = LAYERS.outer.baseAlpha;
    this.baseMidAlpha = LAYERS.mid.baseAlpha;
    this.baseCoreAlpha = LAYERS.core.baseAlpha;

    // Create sprites — textures assigned later in generateTextures()
    this.outerSprite = new Sprite();
    this.outerSprite.anchor.set(0.5, 0);
    this.outerSprite.alpha = this.baseOuterAlpha;

    this.midSprite = new Sprite();
    this.midSprite.anchor.set(0.5, 0);
    this.midSprite.alpha = this.baseMidAlpha;

    this.coreSprite = new Sprite();
    this.coreSprite.anchor.set(0.5, 0);
    this.coreSprite.alpha = this.baseCoreAlpha;

    // Add in order: outer (behind) → mid → core (front)
    this.addChild(this.outerSprite);
    this.addChild(this.midSprite);
    this.addChild(this.coreSprite);

    this.visible = false;
  }

  /**
   * Generate and cache the gradient textures. Call after renderer is available.
   */
  generateTextures(): void {
    const base = getFlareBaseSize(this.shipClassId, this.sizeMultiplier);

    for (const layerName of LAYER_NAMES) {
      const cfg = LAYERS[layerName];
      const lw = base.w * cfg.sizeScale;
      const lh = base.h * cfg.sizeScale;

      const textures: Texture[] = [];
      for (let f = 0; f < FRAME_COUNT; f++) {
        textures.push(generateFlareTexture(layerName, lw, lh, f));
      }

      if (layerName === 'outer') this.outerTextures = textures;
      else if (layerName === 'mid') this.midTextures = textures;
      else this.coreTextures = textures;
    }

    // Set initial textures
    if (this.outerTextures.length) this.outerSprite.texture = this.outerTextures[1];
    if (this.midTextures.length) this.midSprite.texture = this.midTextures[1];
    if (this.coreTextures.length) this.coreSprite.texture = this.coreTextures[1];

    this.texturesReady = true;
  }

  /**
   * Show the flare (called when thrust activates).
   */
  show(): void {
    this.visible = true;
  }

  /**
   * Hide the flare (called when thrust deactivates).
   */
  hide(): void {
    this.visible = false;
  }

  /**
   * Set intensity multiplier (e.g. 1.3 for boost, 1.0 for normal).
   */
  setIntensity(factor: number): void {
    this.intensity = factor;
  }

  /**
   * Animate the flare layers. Call each frame when visible.
   */
  update(): void {
    if (!this.visible || !this.texturesReady) return;

    this.tickCounter++;
    this.animTime += 0.05;

    const t = this.animTime + this.flickerSeed;
    const intense = this.intensity;

    // Core: fast subtle scale pulse
    const coreScaleY = intense * (1.0 + Math.sin(t * 8) * 0.08);
    const coreScaleX = intense * (1.0 + Math.sin(t * 6 + 0.5) * 0.04);
    this.coreSprite.scale.set(coreScaleX, coreScaleY);
    this.coreSprite.alpha = this.baseCoreAlpha * intense;

    // Mid: slower offset pulse
    const midScaleY = intense * (1.0 + Math.sin(t * 5 + 1.2) * 0.12);
    const midScaleX = intense * (1.0 + Math.sin(t * 4 + 0.8) * 0.05);
    this.midSprite.scale.set(midScaleX, midScaleY);
    this.midSprite.alpha = this.baseMidAlpha * intense;

    // Outer: gentle alpha breathing
    const outerAlpha = this.baseOuterAlpha * intense * (0.85 + Math.sin(t * 3 + 2.4) * 0.15);
    this.outerSprite.alpha = outerAlpha;
    const outerScaleY = intense * (1.0 + Math.sin(t * 2.5 + 1.8) * 0.06);
    this.outerSprite.scale.set(intense, outerScaleY);

    // Flicker: cycle texture frames every 5 ticks
    if (this.tickCounter % 5 === 0) {
      const frame = Math.floor(this.tickCounter / 5) % FRAME_COUNT;
      if (this.coreTextures.length) this.coreSprite.texture = this.coreTextures[frame];
      if (this.midTextures.length) this.midSprite.texture = this.midTextures[frame];
      if (this.outerTextures.length) this.outerSprite.texture = this.outerTextures[frame];
    }

    // Organic lateral jitter every 6 ticks
    if (this.tickCounter % 6 === 0) {
      const jitter = (Math.random() - 0.5) * 1.0;
      this.coreSprite.position.x = jitter * 0.5;
      this.midSprite.position.x = jitter * 0.3;
    }
  }
}
